import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api, type AuthMode, type MeResponse, type PreviewUser } from "@/lib/api";
import { clearBrowsingState } from "@/components/list-controls";
import {
  clearAuthTokens,
  readAuthToken,
  readPreviewSubject,
  writeAuthToken,
  writePreviewSubject,
  writeRefreshToken,
  writeOrganizationId,
} from "@/auth/auth-storage";

type AuthState = {
  loading: boolean;
  error: string | null;
  mode: AuthMode | null;
  me: MeResponse | null;
  previewUsers: PreviewUser[];
  selectedPreviewSubject: string;
  previewMode: boolean;
  authRequired: boolean;
  sessionExpired: boolean;
};

type AuthContextValue = AuthState & {
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  setPreviewSubject: (subject: string) => void;
  setOrganization: (organizationId: number) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const generation = useRef(0);
  const [state, setState] = useState<AuthState>(() => ({
    loading: true,
    error: null,
    mode: null,
    me: null,
    previewUsers: [],
    selectedPreviewSubject: readPreviewSubject(),
    previewMode: false,
    authRequired: false,
    sessionExpired: false,
  }));

  const bootstrap = useCallback(async (): Promise<void> => {
    const currentGeneration = ++generation.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const mode = await api.authMode();
      if (generation.current !== currentGeneration) return;

      if (!mode.preview_auth_mode) {
        const token = readAuthToken();
        if (!token) {
          setState({
            loading: false,
            error: null,
            mode,
            me: null,
            previewUsers: [],
            selectedPreviewSubject: "",
            previewMode: false,
            authRequired: true,
            sessionExpired: false,
          });
          return;
        }
        const me = await api.me();
        if (generation.current !== currentGeneration) return;
        setState({
          loading: false,
          error: null,
          mode,
          me,
          previewUsers: [],
          selectedPreviewSubject: "",
          previewMode: false,
          authRequired: false,
          sessionExpired: false,
        });
        return;
      }

      const previewUsers = await api.previewUsers();
      if (generation.current !== currentGeneration) return;
      const storedSubject = readPreviewSubject();
      const subject =
        previewUsers.find((u) => u.identity_subject === storedSubject)?.identity_subject ??
        previewUsers[0]?.identity_subject ??
        "";

      writePreviewSubject(subject);

      const me = subject ? await api.me() : null;
      if (generation.current !== currentGeneration) return;
      setState({
        loading: false,
        error: null,
        mode,
        me,
        previewUsers,
        selectedPreviewSubject: subject,
        previewMode: true,
        authRequired: false,
        sessionExpired: false,
      });
    } catch (error) {
      if (generation.current !== currentGeneration) return;
      setState((prev) => ({
        ...prev,
        me: null,
        loading: false,
        error: error instanceof Error ? error.message : "Unknown auth error",
        authRequired: !prev.previewMode,
      }));
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    function handleAuthExpired(): void {
      ++generation.current;
      queryClient.clear();
      setState((prev) => ({ ...prev, loading: false, me: null, authRequired: true, sessionExpired: true }));
    }
    window.addEventListener("esumei:auth-expired", handleAuthExpired);
    return () => window.removeEventListener("esumei:auth-expired", handleAuthExpired);
  }, [queryClient]);

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      const res = await api.login(email, password);
      clearBrowsingState();
      writeAuthToken(res.id_token);
      writeRefreshToken(res.refresh_token);
      writeOrganizationId("");
      queryClient.clear();
      const me = await api.me();
      setState((prev) => ({
        ...prev,
        me,
        authRequired: false,
        sessionExpired: false,
        error: null,
      }));
    },
    [queryClient],
  );

  const logout = useCallback((): void => {
    ++generation.current;
    clearBrowsingState();
    clearAuthTokens();
    writeOrganizationId("");
    setState((prev) => ({ ...prev, me: null, authRequired: true, sessionExpired: false }));
    queryClient.clear();
  }, [queryClient]);

  const setPreviewSubject = useCallback(
    (subject: string): void => {
      if (!state.previewUsers.some((user) => user.identity_subject === subject)) return;
      ++generation.current;
      clearBrowsingState();
      setState((prev) => ({ ...prev, me: null, loading: true, selectedPreviewSubject: subject }));
      void queryClient.cancelQueries();
      queryClient.clear();
      writeOrganizationId("");
      writePreviewSubject(subject);
      void bootstrap();
    },
    [queryClient, bootstrap, state.previewUsers],
  );

  const setOrganization = useCallback(
    (organizationId: number): void => {
      if (state.loading || organizationId === state.me?.tenant_id ||
          !state.me?.organizations.some((organization) => organization.tenant_id === organizationId)) return;
      ++generation.current;
      clearBrowsingState();
      // Unmount tenant-bound routes before loading the new identity. This also
      // discards open dialogs, unsaved settings and local filter state.
      setState((prev) => ({ ...prev, me: null, loading: true }));
      void queryClient.cancelQueries();
      writeOrganizationId(String(organizationId));
      queryClient.clear();
      void bootstrap();
    },
    [queryClient, bootstrap, state.loading, state.me],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      loginWithPassword,
      logout,
      refresh: bootstrap,
      setPreviewSubject,
      setOrganization,
    }),
    [state, loginWithPassword, logout, bootstrap, setPreviewSubject, setOrganization],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
